import type { FC } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';

import {
    OverviewSection as UIOverviewSection,
    SmallCardContainer,
    getTotalWorthString,
    Toast,
} from '@catalyze/ui';
import { useGetLeadsWorthQuery } from 'api/leads/getLeadsWorth';
import { updateLead } from 'api/leads/updateLead';
import type { ILead, Status } from 'api/types/lead';
import { Card } from 'components/Card';
import { CategoryRect } from 'components/CategoryRectangle';
import { LeadGroups } from 'constants/leadGroups';
import { Routes } from 'constants/routes';

import { EmptyCard } from './components/EmptyCard';
import { NetWorth, Worth, Title } from './styled';

const mappedLinkRoutes: { [k in LeadGroups]?: Routes } = {
    [LeadGroups.RECOMMENDED]: Routes.RECOMMENDED,
    [LeadGroups.CONTACTED]: Routes.CONTACTED,
    [LeadGroups.ACTIVE_CONVERSATION]: Routes.ACTIVE_CONVERSATIONS,
    [LeadGroups.LISTED]: Routes.LISTED,
    [LeadGroups.SOLD]: Routes.SOLD,
};

interface IOverviewSection {
    type: LeadGroups;
    data: ILead[];
    isLoading: boolean;
    status: Status;
    count: Number | undefined;
}

export const OverviewSection: FC<IOverviewSection> = ({
    type,
    data,
    isLoading,
    status,
    count,
}) => {
    const queryClient = useQueryClient();

    const route = mappedLinkRoutes[type] ?? '';
    const { data: leadsNetWorth } = useGetLeadsWorthQuery({ status });
    const commissionWorth =
        leadsNetWorth?.estimatedMarketValue &&
        parseFloat(leadsNetWorth?.estimatedMarketValue) * 0.03;

    return (
        <UIOverviewSection
            data={data}
            isLoading={isLoading}
            type={type}
            sectionRoute={route}
            count={count}
            Title={() => (
                <>
                    <CategoryRect type={type} />
                    <Title>{type}</Title>
                    <NetWorth>
                        <Worth>{`${getTotalWorthString(
                            leadsNetWorth?.estimatedMarketValue
                        )}`}</Worth>
                        Estimated Market Value
                    </NetWorth>
                    <NetWorth>
                        <Worth>{`${getTotalWorthString(
                            commissionWorth
                        )}`}</Worth>
                        Estimated Commission
                    </NetWorth>
                </>
            )}
            emptyStateElement={
                <>
                    <EmptyCard type={type} disableOnHover />
                    <SmallCardContainer
                        disableOnHover
                        backgroundOpacity={0.75}
                    />
                    <SmallCardContainer
                        disableOnHover
                        backgroundOpacity={0.5}
                    />
                    <SmallCardContainer
                        disableOnHover
                        backgroundOpacity={0.25}
                    />
                </>
            }
            renderLaneCard={(property: ILead) => (
                <Card
                    key={property.propertyLeadId}
                    type={type}
                    data={property}
                    onCheckboxChange={async (checked: boolean) => {
                        await updateLead({
                            id: property.propertyLeadId,
                            data: {
                                status: checked ? 'Contacted' : 'Recommended',
                            },
                        });
                        await queryClient.invalidateQueries('property-leads');
                        toast(
                            () => (
                                <Toast title="Property marked as contacted." />
                            ),
                            {
                                style: { backgroundColor: '#000' },
                            }
                        );
                    }}
                />
            )}
        />
    );
};
